import { jsx } from "react/jsx-runtime";
import { css } from "@/styled-system/css";
import { token } from "@/styled-system/tokens";
import { Card } from "@zillow/constellation-10";
import React from "react";
import { v8ResponsiveToPanda } from "../../utils/token-migration/migrate-responsive.esm.js";
import { migrateColorToken, migrateSpacingToken } from "../../utils/token-migration/v8-to-v10.esm.js";
function resolveValue(value) {
  if (!value) return void 0;
  if (typeof value === "string") return value;
  return value.default ?? Object.values(value)[0];
}
function resolveSpacingVar(value) {
  if (value === void 0 || value === null) return void 0;
  const raw = typeof value === "string" ? value : value.default ?? Object.values(value)[0];
  if (!raw) return "0";
  const v10 = migrateSpacingToken(raw) ?? raw;
  return token.var(`spacing.${v10}`);
}
function resolveColorVar(value) {
  if (!value) return void 0;
  const v10 = migrateColorToken(value) ?? value;
  return token.var(`colors.${v10}`);
}
function resolveSpacingToken(v) {
  if (!v) return void 0;
  if (v === "0") return "none";
  return migrateSpacingToken(v) ?? v;
}
function resolveSpacingCss(value) {
  if (value === void 0 || value === null) return void 0;
  if (typeof value === "object" && Object.keys(value).length === 0) return "none";
  if (typeof value === "string") return resolveSpacingToken(value);
  const keys = Object.keys(value);
  if (keys.length === 1 && "default" in value) {
    return resolveSpacingToken(value.default);
  }
  return v8ResponsiveToPanda(value, resolveSpacingToken);
}
function buildCardPaddingStyles(paddingTop, paddingRight, paddingBottom, paddingLeft) {
  const styles = {};
  const pt = resolveSpacingCss(paddingTop);
  const pr = resolveSpacingCss(paddingRight);
  const pb = resolveSpacingCss(paddingBottom);
  const pl = resolveSpacingCss(paddingLeft);
  if (pt !== void 0) styles.paddingTop = pt;
  if (pr !== void 0) styles.paddingRight = pr;
  if (pb !== void 0) styles.paddingBottom = pb;
  if (pl !== void 0) styles.paddingLeft = pl;
  return styles;
}
const cardLayoutStyles = css.raw({
  display: "flex",
  flexDirection: "column",
  // v10 `Card` only renders children directly when there is no whole-card
  // link. As soon as `blockAnchorUrl` is set it wraps them in a
  // `Card.Content` div (the sibling of `Card.BlockAnchor`, which owns the
  // stretched overlay anchor). `cardContentStyles` in the DS sets only
  // position/z-index/pointer-events, so that wrapper is a `display: block`
  // box at content height — it absorbs the column stretch above and hands
  // its children no free space. A Group child with `flex: 1` +
  // `justify-content: flex-end` then has nothing to distribute, so CTAs
  // stop pinning to the bottom of a stretched card (MOPS-30083 → MOPS-30530
  // fixed Card.Root's own `display: flex`; this is the next link in the
  // chain). v8's Card had no such wrapper: children were direct flex items.
  // Restore that by letting the wrapper grow and pass the column through.
  // Targeted by data attribute because production Panda class names are
  // hashed (`hash: optimize`).
  '& > [data-c11n-component="Card.Content"]': {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1
  }
});
const ColorCard = React.forwardRef(({
  backgroundColor,
  fontColor,
  paddingTop,
  paddingRight,
  paddingBottom,
  paddingLeft,
  style,
  className,
  children,
  css: cssProp,
  ...rest
}, ref) => {
  const bgVar = resolveColorVar(resolveValue(backgroundColor));
  const fgVar = resolveColorVar(resolveValue(fontColor));
  const ptVar = resolveSpacingVar(paddingTop);
  const prVar = resolveSpacingVar(paddingRight);
  const plVar = resolveSpacingVar(paddingLeft);
  const cssVars = {};
  if (ptVar) {
    cssVars["--_cimg-mt"] = `calc(-1 * ${ptVar})`;
    cssVars["--_cimg-mb"] = ptVar;
  }
  if (prVar) cssVars["--_cimg-mr"] = `calc(-1 * ${prVar})`;
  if (plVar) cssVars["--_cimg-ml"] = `calc(-1 * ${plVar})`;
  const mergedStyle = {
    ...bgVar ? {
      backgroundColor: bgVar
    } : {},
    ...fgVar ? {
      color: fgVar
    } : {},
    ...cssVars,
    ...style
  };
  const paddingStyles = buildCardPaddingStyles(paddingTop, paddingRight, paddingBottom, paddingLeft);
  return /* @__PURE__ */ jsx(Card, { ref, "data-zrs-color-card": true, className, style: mergedStyle, ...rest, css: css.raw(cardLayoutStyles, paddingStyles, cssProp), children });
});
ColorCard.displayName = "ColorCard";
export {
  buildCardPaddingStyles,
  cardLayoutStyles,
  ColorCard as default
};
